import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowPathIcon } from '@heroicons/react/24/outline';
import { conversationAuditAPI } from '../../services/api';
import { formatDateTime, plural } from '../../utils/format';

const RANGES = [7, 14, 30, 90];

const SEVERITY = {
  high: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200',
  medium: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200',
  low: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200',
};

// Where each kind of hole gets fixed, inside the customer's workspace.
// Blocked answers and restated questions need reading first, so they link
// to the conversation only.
const FIX = {
  topic: { label: 'Add to knowledge', path: 'rag/sources' },
  thin_knowledge: { label: 'Add to knowledge', path: 'rag/sources' },
  declined: { label: 'Agent settings', path: 'agent-management' },
};

// Everything that was not a normal answer, each with its own heading, so an
// agent is never shown as clean while something else went wrong.
const OTHER_OUTCOMES = [
  ['technical_examples', 'Knowledge search failed'],
  ['blocked_examples', 'Answer blocked by the fact check'],
  ['declined_examples', 'Declined as out of scope'],
  ['similar_followups_examples', 'Restated the previous question'],
];

const agentSummary = (agent) => [
  `${plural(agent.questions, 'question')} in ${plural(agent.conversations, 'conversation')}`,
  `${agent.unanswered} with nothing to answer from (${Math.round(agent.unanswered_rate * 100)}%)`,
  agent.blocked ? `${agent.blocked} blocked by the fact check` : null,
  agent.technical ? `${agent.technical} search failures` : null,
  agent.declined ? `${agent.declined} declined` : null,
].filter(Boolean).join(', ');

const emptyAgentText = (agent) => {
  if (agent.unanswered || agent.blocked || agent.technical || agent.declined) {
    return 'No knowledge gaps: the other problems are listed above.';
  }
  return 'Every question in this period got an answer.';
};

const conversationLink = (tenantId, conversationId) => `/t/${tenantId}/conversations?open=${encodeURIComponent(conversationId)}`;

const Tile = ({ label, value, hint, tone = 'text-gray-900 dark:text-white' }) => (
  <div className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
    <p className="text-sm text-gray-600 dark:text-gray-300">{label}</p>
    <p className={`mt-1 text-2xl font-bold ${tone}`}>{value}</p>
    {hint && <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{hint}</p>}
  </div>
);

const Section = ({ title, description, children }) => (
  <section className="rounded-lg border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
    <div className="border-b border-gray-200 px-4 py-3 dark:border-gray-700">
      <h2 className="text-base font-semibold text-gray-900 dark:text-white">{title}</h2>
      {description && <p className="mt-0.5 text-sm text-gray-600 dark:text-gray-300">{description}</p>}
    </div>
    {children}
  </section>
);

const Empty = ({ children }) => <p className="px-4 py-6 text-sm text-gray-500 dark:text-gray-400">{children}</p>;

const linkClass = 'font-semibold text-blue-700 hover:underline dark:text-blue-300';

const detailText = (requestError, fallback) => {
  const detail = requestError?.response?.data?.detail;
  return typeof detail === 'string' ? detail : fallback;
};

const QuestionList = ({ tenantId, items }) => (
  <ul className="space-y-1">
    {items.map((item, index) => (
      <li key={`${item.conversation_id}-${index}`} className="flex items-start justify-between gap-3 text-sm text-gray-700 dark:text-gray-300">
        <span>&ldquo;{item.question}&rdquo;</span>
        <Link to={conversationLink(tenantId, item.conversation_id)} className={`shrink-0 ${linkClass}`}>Open</Link>
      </li>
    ))}
  </ul>
);

const ConversationAuditPage = () => {
  const [days, setDays] = useState(14);
  const [tenant, setTenant] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await conversationAuditAPI.get({ days });
      setData(response.data);
    } catch (requestError) {
      setError(detailText(requestError, 'Could not audit conversations.'));
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => { load(); }, [load]);

  const tenants = useMemo(() => {
    const seen = new Map();
    (data?.agents || []).forEach((agent) => seen.set(agent.tenant_id, agent.tenant_name || agent.tenant_id));
    return [...seen.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  }, [data]);

  const agents = (data?.agents || []).filter((agent) => !tenant || agent.tenant_id === tenant);
  const recommendations = (data?.recommendations || []).filter((rec) => !tenant || rec.tenant_id === tenant);
  const sum = (field) => agents.reduce((total, agent) => total + (agent[field] || 0), 0);
  const questions = sum('questions');
  const unanswered = sum('unanswered');
  const otherFailures = sum('blocked') + sum('technical');
  const rate = questions ? `${Math.round((unanswered / questions) * 100)}% of questions` : 'No questions yet';

  return (
    <div className="mx-auto max-w-7xl space-y-6 p-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Conversation audit</h1>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
            What visitors asked that the agent couldn&rsquo;t answer, grouped by topic, and what to add to fill each hole.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <label className="text-sm text-gray-700 dark:text-gray-200">
            <span className="sr-only">Workspace</span>
            <select
              value={tenant}
              onChange={(event) => setTenant(event.target.value)}
              className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-800"
            >
              <option value="">All workspaces</option>
              {tenants.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
            </select>
          </label>
          <label className="text-sm text-gray-700 dark:text-gray-200">
            <span className="sr-only">Period</span>
            <select
              value={days}
              onChange={(event) => setDays(Number(event.target.value))}
              className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-800"
            >
              {RANGES.map((value) => <option key={value} value={value}>Last {value} days</option>)}
            </select>
          </label>
          <button
            type="button"
            onClick={load}
            className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
          >
            <ArrowPathIcon className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} aria-hidden="true" />
            Refresh
          </button>
        </div>
      </div>

      {error && <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">{error}</div>}
      {data?.truncated && (
        <div role="status" className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
          Too many messages in this period to read them all; showing the first part. Pick a shorter period.
        </div>
      )}

      {data && (
        <>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <Tile label="Visitor questions" value={questions} hint={plural(sum('conversations'), 'conversation')} />
            <Tile label="Nothing to answer from" value={unanswered} hint={`${rate} · ${sum('no_later_message')} with no later message recorded`} tone={unanswered ? 'text-red-600' : undefined} />
            <Tile label="Blocked or failed" value={otherFailures} hint={`${sum('blocked')} blocked by the fact check · ${sum('technical')} search failures`} tone={otherFailures ? 'text-red-600' : undefined} />
            <Tile label="Restated questions" value={sum('similar_followups')} hint="Worth a read, not a failure" />
          </div>

          <Section title="Recommendations" description="Most important first. Each one opens the place to fix it in that workspace.">
            {recommendations.length ? (
              <ul className="divide-y divide-gray-100 dark:divide-gray-700">
                {recommendations.map((rec, index) => {
                  const fix = FIX[rec.kind];
                  return (
                    <li key={`${rec.tenant_id}-${rec.kind}-${rec.title}-${index}`} className="px-4 py-3">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className={`rounded px-1.5 py-0.5 text-xs font-semibold uppercase ${SEVERITY[rec.severity]}`}>{rec.severity}</span>
                        <span className="font-semibold text-gray-900 dark:text-white">{rec.title}</span>
                        <span className="text-sm text-gray-500 dark:text-gray-400">{rec.tenant_name} · {rec.agent_name}</span>
                      </div>
                      <p className="mt-1 text-sm text-gray-700 dark:text-gray-300">{rec.detail}</p>
                      <div className="mt-1.5 flex flex-wrap gap-4 text-sm">
                        {fix && <Link to={`/t/${rec.tenant_id}/${fix.path}`} className={linkClass}>{fix.label}</Link>}
                        {rec.conversation_id && (
                          <Link to={conversationLink(rec.tenant_id, rec.conversation_id)} className={linkClass}>Open a conversation</Link>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            ) : <Empty>No holes found in this period.</Empty>}
          </Section>

          {agents.map((agent) => (
            <Section
              key={`${agent.tenant_id}-${agent.agent_id}`}
              title={`${agent.tenant_name} · ${agent.agent_name}`}
              description={agentSummary(agent)}
            >
              {OTHER_OUTCOMES.some(([field]) => agent[field]?.length) && (
                <div className="space-y-4 border-b border-gray-100 p-4 dark:border-gray-700">
                  {OTHER_OUTCOMES.filter(([field]) => agent[field]?.length).map(([field, heading]) => (
                    <div key={field}>
                      <p className="text-sm font-semibold text-gray-900 dark:text-white">{heading}</p>
                      <div className="mt-1"><QuestionList tenantId={agent.tenant_id} items={agent[field]} /></div>
                    </div>
                  ))}
                </div>
              )}
              {agent.topics.length || agent.one_offs.length ? (
                <div className="space-y-4 p-4">
                  {agent.topics.map((topic) => (
                    <div key={topic.topic}>
                      <p className="text-sm font-semibold text-gray-900 dark:text-white">
                        &ldquo;{topic.topic}&rdquo; · asked {topic.count} times
                        {topic.no_later_message > 0 && <span className="font-normal text-gray-500"> · {topic.no_later_message} with no later message</span>}
                      </p>
                      <div className="mt-1"><QuestionList tenantId={agent.tenant_id} items={topic.examples} /></div>
                    </div>
                  ))}
                  {agent.one_offs.length > 0 && (
                    <div>
                      <p className="text-sm font-semibold text-gray-900 dark:text-white">Asked once</p>
                      <div className="mt-1"><QuestionList tenantId={agent.tenant_id} items={agent.one_offs} /></div>
                    </div>
                  )}
                </div>
              ) : <Empty>{emptyAgentText(agent)}</Empty>}
            </Section>
          ))}

          <p className="text-xs text-gray-500 dark:text-gray-400">
            Read from saved conversations since {formatDateTime(data.since)}. &ldquo;Nothing to answer from&rdquo; is
            what the agent recorded when its knowledge had nothing on the question. &ldquo;Blocked&rdquo; means it wrote
            a price or claim the fact check could not support. &ldquo;No later message&rdquo; only means nothing was
            saved after that reply; it does not show whether the visitor left.
          </p>
        </>
      )}
    </div>
  );
};

export default ConversationAuditPage;
